// # @maru/platform — 플랫폼이 다른 부분만 모아 둔 곳
//
// 웹·데스크톱·모바일이 다르게 동작해야 하는 지점은 생각보다 적다. 알림, 파일 저장,
// 로컬 보관, 딥링크, 배지 정도다. **그 전부를 이 파일의 인터페이스로 좁혀 둔다.**
//
// ## 왜 이렇게까지 하는가
// 이 경계가 없으면 `if (window.__TAURI__)` 가 컴포넌트 안에 흩어진다. 그러면
// 웹에서 데스크톱 코드가, 데스크톱에서 웹 코드가 번들에 섞이고, 시험할 때는
// 둘 다 가짜로 만들어야 한다. 경계를 한 곳에 두면 **UI 는 플랫폼을 아예 모른다.**

/** 브라우저 밖에서도 살아남아야 하는 것들. */
export interface Platform {
  readonly name: '웹' | '데스크톱' | '모바일'

  /** 토큰처럼 **디스크에 남으면 안 되는 것**은 여기에 두지 않는다. */
  keep: Keeper

  /** 안전한 자격 증명 보관(키체인·Credential Manager). 웹에는 없다. */
  vault: Vault | null

  notify(to_notify: NotifyPayload): Promise<void>

  /**
   * 이 기기가 알림을 띄워도 되는가. **묻지 않고 읽기만 한다** — 권한 창은 `notify` 가 첫 알림 때 띄운다.
   * 셸은 거절과 아직 안 물음을 가르지 못해 둘 다 `default` 다.
   */
  alerts(): Promise<AlertPermission>

  /** 앱 아이콘의 숫자. 웹은 파비콘/타이틀, 데스크톱은 독, 모바일은 배지. */
  badge(count: number): void

  /**
   * 지금 보는 자리(`#디자인 - 마루 팀`). 웹은 탭 제목에 싣는다 — 탭 여럿이 전부 "마루" 면 어느 탭이
   * 어느 채널인지 눌러 봐야 안다(3차 F219). `null` 은 자리 없음. 셸은 창 제목이 하나라 안 해도 된다.
   */
  place?(label: string | null): void

  /** 창이 지금 보이는가. 안 보이면 알림을 띄우고, 보이면 안 띄운다. */
  visible(): boolean

  open_file(route: string): Promise<void>

  /** 바깥 주소를 연다. 웹은 새 탭, 셸은 시스템 브라우저 — 웹뷰 안에서 남의 페이지를 띄우지 않는다. */
  open_url(url: string): Promise<void>

  /**
   * 바깥에서 들어온 좌표(`#/c/7/42`)를 듣는다. 셸은 `maru://` 딥링크, 웹은 알림 누르기.
   * 돌려준 함수로 끊는다.
   */
  links(listen: (hash: string) => void): () => void
  /** 앱만 — 시스템 브라우저에서 끝낸 로그인이 딥링크로 돌아오면 그 주소를 준다. 웹은 없다(같은 탭으로 돌아온다). */
  auth_back?(hear: (url: string) => void): () => void

  /**
   * 꺼진 앱을 깨울 공급자 토큰. 폰 셸은 APNs·FCM, 브라우저는 웹 푸시(VAPID) 구독.
   * 워커나 푸시 API 가 없는 곳(데스크톱 셸·옛 브라우저)은 `null`.
   */
  push: PushSource | null
}

/** 알림 권한. `unsupported` 는 알림 API 자체가 없는 곳(옛 브라우저). */
export type AlertPermission = 'granted' | 'denied' | 'default' | 'unsupported'

/** 서버 `PUT /v1/me/push-devices` 에 그대로 거는 모양. */
export interface PushToken {
  kind: 'apns' | 'fcm' | 'web'
  token: string
}

export interface PushSource {
  /**
   * 웹 푸시는 서버의 VAPID 공개 키로 구독한다. 참이면 `token` 에 그 키를 넘겨야 하고,
   * 서버가 키를 안 주면(웹 푸시를 안 켰다) 부르지 않는다.
   */
  needs_key?: true
  /** 지금 토큰. 권한을 거절했거나 아직 못 받았으면 `null` — 그때는 조용히 넘어간다. */
  token(key?: string): Promise<PushToken | null>
  /** 공급자가 토큰을 갈았다(앱 복원·재설치). 돌려준 함수로 끊는다. */
  changed(listen: (fresh: PushToken) => void): () => void
}

export interface Keeper {
  get(key: string): Promise<string | null>
  put(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
}

export interface Vault {
  get(key: string): Promise<string | null>
  put(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
}

export interface NotifyPayload {
  subject: string
  body: string
  /** 눌렀을 때 갈 좌표. 영구 링크와 같은 해시다 — `#/c/42/1337` */
  deep_link?: string
}

/**
 * 시험용. **모든 호출을 기록한다.**
 *
 * 컴포넌트 시험에서 "알림이 떴는가" 를 확인하는 것이 목적이지,
 * 진짜 알림을 띄우는 것이 목적이 아니다.
 */
export function FakePlatform(): Platform & { record: string[] } {
  const record: string[] = []
  const memory = new Map<string, string>()
  return {
    name: '웹',
    record,
    keep: {
      get: async (k) => memory.get(k) ?? null,
      put: async (k, v) => void memory.set(k, v),
      remove: async (k) => void memory.delete(k),
    },
    vault: null,
    notify: async (a) => void record.push(`notify:${a.subject}`),
    alerts: async () => 'granted',
    badge: (n) => void record.push(`badge:${n}`),
    visible: () => true,
    open_file: async (p) => void record.push(`open:${p}`),
    open_url: async (u) => void record.push(`url:${u}`),
    links: () => () => {},
    push: null,
  }
}

/** 손에 드는 기기인가. Tauri 는 iOS·Android 에서도 같은 웹뷰를 띄우므로 UA 로 가른다. */
export function is_handheld(ua: string): boolean {
  return /iPhone|iPad|iPod|Android/.test(ua)
}

/**
 * 기기 목록에 사람이 알아볼 한 줄. 긴 UA 를 그대로 보내면 목록이 못 읽을 글이 된다.
 * `shell` 은 웹뷰를 감싼 것 — 브라우저면 이름을 UA 에서 읽고, 앱이면 '앱' 이라 적는다.
 */
export function device_label(ua: string, shell: '웹' | '앱'): string {
  const box = /iPhone|iPad|Android|Macintosh|Windows|Linux/.exec(ua)?.[0]
  const browser =
    shell === '웹' ? /Firefox\/|Edg\/|Chrome\/|Safari\//.exec(ua)?.[0]?.replace(/[/].*/, '') : '앱'
  return [box, browser].filter(Boolean).join(' ') || shell
}

/**
 * `maru://c/7/42` 를 앱이 읽는 해시(`#/c/7/42`)로. 모양이 아니면 `null` —
 * 딥링크는 아무 앱이나 쏠 수 있으므로 좌표 말고는 받지 않는다.
 */
export function deep_to_hash(url: string): string | null {
  const m = /^maru:\/\/c\/(\d{1,15})\/(\d{1,15})\/?$/.exec(url.trim())
  return m === null ? null : `#/c/${m[1]}/${m[2]}`
}

/** 바깥으로 보내도 되는 주소인가. `javascript:`·`file:` 은 시스템에 넘기지 않는다. */
export function is_outward(url: string): boolean {
  try {
    const { protocol } = new URL(url)
    return protocol === 'https:' || protocol === 'http:' || protocol === 'mailto:'
  } catch {
    return false
  }
}
